import React, { useContext, useEffect, useState } from "react";
import { Avatar, Box, Button, Grid, IconButton, List, ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import PaymentsIcon from "@material-ui/icons/AccountBalanceWallet";
import BusinessIcon from "@material-ui/icons/Business";
import AmountIcon from "@material-ui/icons/LocalAtm";
import StatusIcon from '@material-ui/icons/TurnedInNot';
import MoreVertIcon from "@material-ui/icons/MoreVert";
import DownloadIcon from "@material-ui/icons/GetAppTwoTone";

import translate from "../i18n/Translator";
import { getPayment as getCustomerPayment, getPaymentUrl as getCustomerPaymentUrl } from "../api/CustomerPaymentAPI";
import { getPayment, getPaymentUrl } from "../api/PaymentAPI";
import { AppContext } from "../context/AppContext";
import { ErrorSnackbar, SuccessSnackbar } from "../components/Snackbars";
import { Payment } from "../model/Payment";
import Progress from "../components/Progress";
import Ellipsis from "../components/Ellipsis";
import DateFormat from "../components/DateFormat";
import { RouterParams } from "../router/RouterParams";
import Surface from "../components/Surface";
import Gridable from "../components/Gridable";
import { Cfdi } from "../model/Cfdi";
import { Redirect } from "react-router-dom";
import CurrencyFormat from "../components/CurrencyFormat";
import { APP_COLORS } from "../components/Colors";
import PaymentMenu from "./PaymentMenu";
import RegisterTransactionPopup from "./RegisterTransactionPopup";
import DeletePaymentPopup from "./DeletePaymentPopup";
import NewPaymentPopup from "./NewPaymentPopup";
import ConciliatePaymentPopup from "./ConciliatePaymentPopup";
import InvalidatePaymentPopup from "./InvalidatePaymentPopup";

export default function PaymentViewer({ match }: RouterParams) {
    const context = useContext(AppContext);
    const theme = useTheme();
    const isXs = useMediaQuery(() => theme.breakpoints.down("xs"));

    const [status, setStatus] = useState<string>("loading");
    const [data, setData] = useState<Payment>();
    const [redirect, setRedirect] = useState<string>();
    const [error, setError] = useState<string>();
    const [success, setSuccess] = useState<string>();
    const [color, setColor] = useState<string>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [dialog, setDialog] = useState<"conciliate" | "invalidate" | "register" | "refresh" | "delete">();
    const [selectedForPayment, setSelectedForPayment] = useState<Cfdi[]>();
    const [url, setUrl] = useState<string>();

    const retrievePromise = () => {
        const customerId = context.session?.customer?.id;
        if (customerId) {
            return getCustomerPayment(context.session!.tenant!.id, customerId, match.params.paymentId)
        }
        return getPayment(context.session!.tenant!.id, match.params.paymentId);
    };

    const fileUrlPromise = () => {
        const customerId = context.session?.customer?.id;
        if (customerId) {
            return getCustomerPaymentUrl(context.session!.tenant!.id, customerId, match.params.paymentId)
        }
        return getPaymentUrl(context.session!.tenant!.id, match.params.paymentId);
    };

    const load = () => {
        setStatus("loading")
        retrievePromise().then(response => {
            setData(response);

            switch (response.status) {
                case "VALIDATING":
                    setColor(APP_COLORS.info);
                    break;
                case "COMPLETED":
                    setColor(APP_COLORS.success);
                    break;
                case "INVALID":
                    setColor(APP_COLORS.error);
                    break;
                case "EXPIRED":
                    setColor(APP_COLORS.warning);
                    break;
                default:
                    break;
            }

            if (response.transaction && response.transaction.file) {
                fileUrlPromise().then(url => {
                    setUrl(url);
                }).catch(error => {
                });
            }

            setStatus("loaded")
        }).catch(error => {
            setStatus(error.message);
        });
    };

    useEffect(load, [match.params.paymentId]);

    const identifier = (cfdi: Cfdi) => {
        if (cfdi.serie || cfdi.folio) {
            return `${cfdi.serie || ""}${cfdi.serie ? "-" : ""}${cfdi.folio || ""}`;
        }
        if (cfdi.complementos && cfdi.complementos.timbre_fiscal_digital) {
            const uuid = cfdi.complementos.timbre_fiscal_digital.uuid;
            return uuid.substring(uuid.length - 10);
        }
        return "---";
    };

    const onClickedMore = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const onRefreshPayment = () => {
        if (!data) return;

        setAnchorEl(null);
        const selected = data.payment_cfdis.filter(paymentCfdi => paymentCfdi.cfdi !== undefined).map(paymentCfdi => {
            const cfdi = paymentCfdi.cfdi!;
            if (cfdi.metadata) {
                cfdi.metadata.payment_amount = paymentCfdi.amount;
            }
            return cfdi;
        });
        setSelectedForPayment(selected);
        setDialog("refresh");
    };

    const onRefreshedPayment = (payment: Payment) => {
        onCancelPayment();
        setSuccess(translate("payments.edit.success") as string);
        load();
    };

    const onRegisterPayment = () => {
        setAnchorEl(null);
        setDialog("register");
    };

    const onRegisteredPayment = (payment: Payment) => {
        setDialog(undefined);
        setSuccess(translate("payments.transaction.success") as string);
        load();
    };

    const onCancelPayment = () => {
        setSelectedForPayment(undefined);
        onCloseOption();
    };

    const onDeletePayment = () => {
        setAnchorEl(null);
        setDialog("delete");
    };

    const onDeletedPayment = () => {
        setDialog(undefined);
        setSuccess(translate("payments.delete.success") as string);
        setRedirect("/payments");
    };

    const onCloseOption = () => {
        setAnchorEl(null);
        setDialog(undefined);
    };

    const onConciliatePayment = () => {
        setDialog("conciliate");
    };

    const onInvalidatePayment = () => {
        setDialog("invalidate");
    };

    const onCompletedPayment = (payment: Payment) => {
        setRedirect("/payments/pending");
    };

    const onClosedSnackbars = () => {
        setSuccess(undefined);
        setError(undefined);
    };

    if (redirect) {
        return (
            <Redirect to={redirect} />
        )
    }

    if (status === "loading" || !data) {
        return <Progress />;
    }

    if (status !== "loaded") {
        return (
            <Typography variant="body1" component="h5" color="error" align="center">
                {status}
            </Typography>
        );
    }

    return (
        <Surface title={data.customer ? data.customer.name : undefined}
            subtitle={translate("payments.detail")}
            icon={<PaymentsIcon />} backButton
            titleActions={context.isGrantedAny(["CustomersPaymentsUpdate", "CustomersPaymentsDelete"]) && (data.status === "NEW" || data.status === "EXPIRED") ? (
                <Grid item xs="auto">
                    <IconButton color="default" size="small" onClick={onClickedMore}>
                        <MoreVertIcon />
                    </IconButton>
                </Grid>
            ) : undefined}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <List>
                        {data.customer && data.customer.external_id && (
                            <ListItem>
                                <ListItemAvatar>
                                    <Avatar>
                                        <BusinessIcon />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText primary={data.customer?.external_id || "---"} secondary={translate("customers.external_id")} />
                            </ListItem>
                        )}
                        <ListItem>
                            <ListItemAvatar>
                                <Avatar>
                                    <AmountIcon />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText primary={(
                                <CurrencyFormat amount={data.total} currency={data.currency} />
                            )} secondary={translate("payments.total")} />
                        </ListItem>
                        <ListItem>
                            <ListItemAvatar>
                                <Avatar style={color ? { "backgroundColor": color } : undefined}>
                                    <StatusIcon />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText primary={translate(`payments.status.${data.status}`)} secondary={translate("payments.status.title")} />
                        </ListItem>
                    </List>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <List dense>
                        <ListItem>
                            <ListItemText primary={(
                                <DateFormat date={data.transaction?.date || data.expires_at || data.created_at} format="YYYY-MMM-DD" />
                            )} secondary={translate("payments.date")} />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary={data.account} secondary={translate("payments.transaction.account")} />
                        </ListItem>
                        {data.transaction && (
                            <ListItem>
                                <ListItemText primary={translate(`payments.form.${data.transaction.form}`)} secondary={translate("payments.form.title")} />
                            </ListItem>
                        )}
                        {data.transaction && data.transaction.reference && (
                            <ListItem>
                                <ListItemText primary={data.transaction.reference} secondary={translate("payments.transaction.reference")} />
                            </ListItem>
                        )}
                        {data.transaction && data.transaction.check_number && (
                            <ListItem>
                                <ListItemText primary={data.transaction.check_number} secondary={translate("payments.transaction.check_number")} />
                            </ListItem>
                        )}
                        {data.transaction && data.transaction.file && (
                            <ListItem>
                                <ListItemText primary={translate("payments.transaction.file")} />
                                <ListItemSecondaryAction>
                                    <IconButton component="a" href={url || "#"} disabled={!url} target="_blank" rel="noopener noreferrer">
                                        <DownloadIcon color="primary" />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        )}
                    </List>
                </Grid>
                <Grid item xs={12}>
                    <Gridable
                        items={data.payment_cfdis}
                        loading={false}
                        empty={translate("cfdis.empty") as string}
                        columns={[
                            {
                                title: translate("cfdis.id") as string,
                                converter: (paymentCfdi) => (
                                    <div>
                                        <Ellipsis text={identifier(paymentCfdi.cfdi!)} lenght={100} uppercased={false} /><br />
                                        <Ellipsis text={translate(`cfdis.tipo_comprobante.${paymentCfdi.cfdi!.tipo_comprobante}`) as string} lenght={0} secondary />
                                    </div>
                                ),
                                xs: true
                            },
                            {
                                title: translate(data.status === "COMPLETED" ? "payments.new.paid_amount" : "payments.new.amount") as string,
                                converter: (paymentCfdi) => (
                                    <CurrencyFormat amount={paymentCfdi.amount} currency={paymentCfdi.currency} />
                                ),
                                xs: false,
                                sm: 2
                            },
                            {
                                title: translate("payments.new.exchange_rate") as string,
                                converter: (paymentCfdi) => (
                                    <CurrencyFormat amount={paymentCfdi.exchange_rate || 1} currency={data.currency} />
                                ),
                                xs: false,
                                sm: 2
                            },
                            {
                                title: translate(data.status === "COMPLETED" ? "payments.new.paid_amount_currency" : "payments.new.amount_currency", { "currency": data.currency }) as string,
                                converter: (paymentCfdi) => (
                                    <CurrencyFormat amount={paymentCfdi.payment} currency={data.currency} />
                                ),
                                xs: 6,
                                sm: 2
                            },
                            {
                                title: translate("payments.balance_post") as string,
                                converter: (paymentCfdi) => (
                                    <CurrencyFormat amount={paymentCfdi.balance_after || 0} currency={paymentCfdi.currency} />
                                ),
                                xs: false,
                                sm: 2
                            }
                        ]}
                    />
                </Grid>
                {context.isGranted("PaymentsUpdate") && data.status === "VALIDATING" && (
                    <Grid item xs={12}>
                        <Box mt={3}>
                            <Grid container justify="flex-end" spacing={1}>
                                <Grid item xs={12} sm="auto">
                                    <Button variant="outlined" color="secondary" size="large" fullWidth={isXs} onClick={onInvalidatePayment}>
                                        {translate("buttons.not_received")}
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm="auto">
                                    <Button variant="contained" color="primary" size="large" fullWidth={isXs} onClick={onConciliatePayment}>
                                        {translate("buttons.conciliate")}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                )}
            </Grid>
            {anchorEl &&
                <PaymentMenu payment={data} anchor={anchorEl} onClose={onCloseOption} skipSee
                    onComplete={onRegisterPayment} onRefresh={onRefreshPayment} onDelete={onDeletePayment} />
            }
            {dialog === "register" && (
                <RegisterTransactionPopup payment={data} onClose={onCloseOption} onSuccess={onRegisteredPayment} />
            )}
            {dialog === "delete" && (
                <DeletePaymentPopup payment={data} onClose={onCloseOption} onSuccess={onDeletedPayment} />
            )}
            {dialog === "refresh" && selectedForPayment && (
                <NewPaymentPopup cfdis={selectedForPayment} paymentId={data.id} currency={data.currency}
                    onClose={onCancelPayment} onSuccess={onRefreshedPayment} />
            )}
            {dialog === "conciliate" && (
                <ConciliatePaymentPopup paymentId={data.id} onClose={onCloseOption} onSuccess={onCompletedPayment} />
            )}
            {dialog === "invalidate" && (
                <InvalidatePaymentPopup paymentId={data.id} onClose={onCloseOption} onSuccess={onCompletedPayment} />
            )}
            <ErrorSnackbar message={error} onClose={onClosedSnackbars} />
            <SuccessSnackbar message={success} onClose={onClosedSnackbars} />
        </Surface>
    );

}